'use client'

import * as React from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { Download, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { usePostPurchaseRulesList } from '@/lib/api/postPurchaseRules'
import { PostPurchaseRulesTable } from './components/PostPurchaseRulesTable'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import { Card, CardContent } from '@/components/ui/card'
import { useTablePage } from '@/hooks/use-table-page'
import {
    postPurchaseRulesFilterMapping,
    postPurchaseRulesSortMapping,
    postPurchaseRulesQueryParamsMapping,
} from './util/table-mapping'

const PostPurchasePage = () => {
    const { t } = useTranslation('postPurchaseRules')
    const { id, navigate, storeRoutes } = useStoreNavigate()
    const { setTableState, queryInput } = useTablePage({
        initialSorting: [{ id: 'createdAt', desc: true }],
        filterMapping: postPurchaseRulesFilterMapping,
        sortMapping: postPurchaseRulesSortMapping,
        queryParamsMapping: postPurchaseRulesQueryParamsMapping,
    })

    const {
        data: rulesResponse,
        isLoading: loading,
        error,
        refetch,
    } = usePostPurchaseRulesList(
        {
            storeId: id,
            page: queryInput.pagination.page,
            per_page: queryInput.pagination.pageSize,
            sort_order: queryInput.sortBy.direction,
        },
        {
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
        }
    )
    const rules = rulesResponse?.items || []
    const total = rulesResponse?.pagination?.totalItems || 0
    return (
        <div className="flex flex-col space-y-4 sm:space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div>
                    <h1 className="text-xl font-semibold sm:text-2xl">{t('title')}</h1>
                    <p className="text-muted-foreground text-sm">{t('description')}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" className="w-full sm:w-auto">
                        <Download className="mr-2 h-4 w-4" />
                        {t('export')}
                    </Button>
                    <Button className="w-full sm:w-auto" onClick={() => navigate(storeRoutes.POST_PURCHASE_CREATE())}>
                        <Plus className="mr-2 h-4 w-4" />
                        {t('createRule')}
                    </Button>
                </div>
            </div>
            <Card>
                <CardContent>
                    <PostPurchaseRulesTable
                        rules={rules}
                        total={total}
                        loading={loading}
                        onTableStateChange={setTableState}
                        onRefetch={refetch}
                    />
                </CardContent>
            </Card>

            {/* Error message */}
            {error && (
                <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
                    {error instanceof Error ? error.message : t('error.loadError')}
                </div>
            )}
        </div>
    )
}

const PostPurchasePageGuarded = withRequiredPermission(PostPurchasePage, PERMISSIONS.STORES_VIEW)
export default PostPurchasePageGuarded
export { PostPurchasePageGuarded as PostPurchasePage }

